import React, { useCallback, useState } from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import theme from "styles/theme"
import AccordionItem from "components/AccordionItem"
import CTAButton from "components/buttons/CTAButton"
import LinkList from "components/LinkList"
import BlockContent from "components/BlockContent"

const AccordionList = ({ title, items }) => {
  const [openItem, setOpenItem] = useState(false)
  const close = useCallback(() => {
    setOpenItem(false)
  }, [setOpenItem])

  return (
    <div
      css={{
        maxWidth: 942,
        margin: "0 auto",
      }}
    >
      <div
        css={{
          marginTop: 68,
          [theme.tablet]: { marginTop: 54 },
          [theme.mobile]: { marginTop: 32 },
        }}
      >
        <h3
          css={{
            ...theme.t4,
            textAlign: "center",
            marginBottom: 26,
            [theme.tablet]: { marginBottom: 23 },
            [theme.mobile]: { marginBottom: 19 },
          }}
        >
          {title}
        </h3>

        {items.map(({ _key, title, content }) => (
          <AccordionItem
            key={_key}
            id={_key}
            title={title}
            isOpen={openItem === _key}
            onRequestOpen={setOpenItem}
            onRequestClose={close}
            css={{
              marginBottom: 20,
              [theme.tablet]: { marginBottom: 15 },
              [theme.mobile]: { marginBottom: 10 },
            }}
          >
            <BlockContent
              css={{
                "& :first-child": { marginTop: 0 },
                "& :last-child": { marginBottom: 0 },
                [["ol", "ul"]]: {
                  ...theme.bodyCopySm,
                  fontWeight: 500,
                  margin: "0 0 0 0.667em",
                  paddingLeft: "1em",
                  [theme.tablet]: { marginLeft: 10, marginRight: 0 },
                  [theme.mobile]: { marginLeft: 8 },
                  li: {
                    paddingLeft: 5,
                    [theme.tablet]: { paddingLeft: 3 },
                    [theme.mobile]: { paddingLeft: 1 },
                  },
                },
                p: {
                  ...theme.bodyCopySm,
                  color: theme.textGray,
                  a: {
                    transition: "color 250ms",
                    ":hover": {
                      color: theme.midBlue,
                    },
                  },
                },
              }}
              serializers={contentSerializers}
            >
              {content}
            </BlockContent>
          </AccordionItem>
        ))}
      </div>
    </div>
  )
}

AccordionList.propTypes = {
  title: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      content: PropTypes.array.isRequired,
    })
  ).isRequired,
}

export default AccordionList

const CTAButtonSerializer = ({ node: { href, text } }) => (
  // Since the top-level element of CTAButton uses inline formatting context
  // to fit to the content, we need to apply margins to a separate block
  // formatting element to allow them to collapse with siblings.
  <div css={{ marginTop: 35, [theme.tablet]: { marginTop: 30 } }}>
    <CTAButton href={href} colorScheme="dark" size="sm">
      {text}
    </CTAButton>
  </div>
)
CTAButtonSerializer.propTypes = {
  node: PropTypes.shape({
    href: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  }).isRequired,
}

/* eslint-disable react/display-name, react/prop-types */
const contentSerializers = {
  types: {
    ctaButton: CTAButtonSerializer,
    linkList: ({ node }) => <LinkList links={node.links} />,
  },
}
/* eslint-enable react/display-name, react/prop-types */

export const query = graphql`
  fragment AccordionContent on SanityAccordionContent {
    _type
    copy
    title
    items {
      _key
      title
      content: _rawContent(resolveReferences: { maxDepth: 3 })
    }
  }
`
